import { Button, Col, List, Avatar, Typography, Space, Tag } from "antd";
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { OrdersInfoSingleDishModal } from "../OrdersInfoSingleDishModal/OrdersInfoSingleDishModal";
import { SimpleReplaceDishModal } from "../SimpleReplaceDishModal";
import { DishType } from "../../../../constants/enums";
import { createOrderActions } from "../../../../store/slices/create-order-slice/create.orders.slice";
import { ImageReusable } from "../../../../ui";
import CustomMenuBuilder from "./CustomMenuBuilder";

const { Text } = Typography;

function RightOrderCreateModel() {
  const dispatch = useDispatch();
  const { selectedDates, packId, dishes, replacedProteinDishes, replacedSauceDishes, isCustomMode } = useSelector(
    (store) => store.createOrder
  );
  const { menuDishesPacksForDates } = useSelector((store) => store.orders);
  const [isShowSingleDish, setIsShowSingleDish] = useState(false);
  const [selectedDishId, setSelectedDishId] = useState(null);
  const [isShowUpdateSingleDish, setIsShowUpdateSingleDish] = useState(false);
  // Protein/Sauce replacement modal state
  const [isShowReplaceProtein, setIsShowReplaceProtein] = useState(false);
  const [isShowReplaceSauce, setIsShowReplaceSauce] = useState(false);
  const [selectedSubDishData, setSelectedSubDishData] = useState(null);
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;

  const getDishesForDate = (date) => {
    if (!packId) return [];

    const packs = menuDishesPacksForDates.find(
      (pack) =>
        dayjs(pack.date).format("YYYY-MM-DD") ===
        dayjs(date).format("YYYY-MM-DD")
    );

    const meals = packs?.packs?.find((p) => p.id === packId)?.meals;

    const dishesForDate = dishes.filter(
      (dish) =>
        dayjs(dish.date).format("YYYY-MM-DD") ===
        dayjs(date).format("YYYY-MM-DD")
    );

    // sort dishesfordate by meals
    const sortedMeals = dishesForDate.sort((a, b) => {
      return (
        meals.findIndex((meal) => meal.id === a.mealId) -
        meals.findIndex((meal) => meal.id === b.mealId)
      );
    });

    return sortedMeals;
  };

  // Get the effective protein dish (considering replacements)
  const getEffectiveProteinDish = (menuDish) => {
    const replacement = replacedProteinDishes.find(
      (r) =>
        r.mealId === menuDish.mealId &&
        dayjs(r.date).format("YYYY-MM-DD") === dayjs(menuDish.date).format("YYYY-MM-DD") &&
        r.packId === packId
    );
    if (replacement) return replacement.dish?.dish || replacement.dish;
    return menuDish.proteinDish;
  };

  // Get the effective sauce dish (considering replacements)
  const getEffectiveSauceDish = (menuDish) => {
    const replacement = replacedSauceDishes.find(
      (r) =>
        r.mealId === menuDish.mealId &&
        dayjs(r.date).format("YYYY-MM-DD") === dayjs(menuDish.date).format("YYYY-MM-DD") &&
        r.packId === packId
    );
    if (replacement) return replacement.dish?.dish || replacement.dish;
    return menuDish.sauceDish;
  };

  const getNutritionalSummaryForDate = (date) => {
    const menuDishes = dishes.filter(
      (dish) =>
        dayjs(dish.date).format("YYYY-MM-DD") ===
        dayjs(date).format("YYYY-MM-DD")
    );
    if (!Array.isArray(menuDishes) || menuDishes.length === 0) {
      return {
        totalEnergy: 0,
        totalProteins: 0,
        totalFats: 0,
        totalCarbs: 0,
        totalWeight: 0,
        proteinPercentage: 0,
        fatPercentage: 0,
        carbPercentage: 0,
      };
    }

    const totals = menuDishes.reduce(
      (acc, menuDish) => {
        const dish = menuDish?.dish || {};
        let energy = Number(dish?.energy) || 0;
        let proteins = Number(dish?.proteins) || 0;
        let fats = Number(dish?.fats) || 0;
        let carbs = Number(dish?.carbohydrates) || Number(dish?.carbs) || 0;
        let weight = Number(dish?.weightNetto) || Number(dish?.weight) || 0;

        // Add protein/sauce dish nutrition for BASE dishes
        if (menuDish?.type === DishType.BASE) {
          const proteinDish = getEffectiveProteinDish(menuDish);
          const sauceDish = getEffectiveSauceDish(menuDish);
          if (proteinDish) {
            energy += Number(proteinDish?.energy) || 0;
            proteins += Number(proteinDish?.proteins) || 0;
            fats += Number(proteinDish?.fats) || 0;
            carbs += Number(proteinDish?.carbohydrates) || Number(proteinDish?.carbs) || 0;
            weight += Number(proteinDish?.weightNetto) || Number(proteinDish?.weight) || 0;
          }
          if (sauceDish) {
            energy += Number(sauceDish?.energy) || 0;
            proteins += Number(sauceDish?.proteins) || 0;
            fats += Number(sauceDish?.fats) || 0;
            carbs += Number(sauceDish?.carbohydrates) || Number(sauceDish?.carbs) || 0;
            weight += Number(sauceDish?.weightNetto) || Number(sauceDish?.weight) || 0;
          }
        }

        return {
          totalEnergy: acc.totalEnergy + energy,
          totalProteins: acc.totalProteins + proteins,
          totalFats: acc.totalFats + fats,
          totalCarbs: acc.totalCarbs + carbs,
          totalWeight: acc.totalWeight + weight,
        };
      },
      {
        totalEnergy: 0,
        totalProteins: 0,
        totalFats: 0,
        totalCarbs: 0,
        totalWeight: 0,
      }
    );

    const proteinCalories = totals.totalProteins * 4;
    const fatCalories = totals.totalFats * 9;
    const carbCalories = totals.totalCarbs * 4;
    const totalMacroCalories = proteinCalories + fatCalories + carbCalories;

    const proteinPercentage =
      totalMacroCalories > 0 ? (proteinCalories / totalMacroCalories) * 100 : 0;
    const fatPercentage =
      totalMacroCalories > 0 ? (fatCalories / totalMacroCalories) * 100 : 0;
    const carbPercentage =
      totalMacroCalories > 0 ? (carbCalories / totalMacroCalories) * 100 : 0;

    return {
      ...totals,
      proteinPercentage,
      fatPercentage,
      carbPercentage,
    };
  };

  const MacronutrientPieChart = ({
    proteinPercentage,
    fatPercentage,
    carbPercentage,
  }) => {
    const size = 40;
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const proteinDash = (proteinPercentage / 100) * circumference;
    const fatDash = (fatPercentage / 100) * circumference;
    const carbDash = (carbPercentage / 100) * circumference;

    const proteinOffset = 0;
    const fatOffset = proteinDash;
    const carbOffset = proteinDash + fatDash;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.5rem",
        }}
      >
        <svg width={size} height={size} style={{ marginBottom: "0.5rem" }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#e0e0e0"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#3498db"
            strokeWidth={strokeWidth}
            strokeDasharray={`${proteinDash} ${circumference}`}
            strokeDashoffset={-proteinOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ fill: "transparent" }}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#f39c12"
            strokeWidth={strokeWidth}
            strokeDasharray={`${fatDash} ${circumference}`}
            strokeDashoffset={-fatOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ fill: "transparent" }}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#27ae60"
            strokeWidth={strokeWidth}
            strokeDasharray={`${carbDash} ${circumference}`}
            strokeDashoffset={-carbOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ fill: "transparent" }}
          />
        </svg>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
            marginTop: "8px",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "2px 8px",
              borderRadius: "6px",
              background: "#f5f6fa",
              fontSize: 13,
              minWidth: 68,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "13px",
                height: "13px",
                borderRadius: "50%",
                backgroundColor: "#3498db",
                marginRight: "4px",
                border: "1px solid #d6eaf8",
              }}
            ></div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: 13,
                  color: "#3498db",
                  marginRight: 2,
                }}
              >
                P
              </span>
              <span style={{ color: "#222", fontSize: 12 }}>
                {Math.round(proteinPercentage)}%
              </span>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "2px 8px",
              borderRadius: "6px",
              background: "#f5f6fa",
              fontSize: 13,
              minWidth: 68,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "13px",
                height: "13px",
                borderRadius: "50%",
                backgroundColor: "#f39c12",
                marginRight: "4px",
                border: "1px solid #fbeee6",
              }}
            ></div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: 13,
                  color: "#f39c12",
                  marginRight: 2,
                }}
              >
                F
              </span>
              <span style={{ color: "#222", fontSize: 12 }}>
                {Math.round(fatPercentage)}%
              </span>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "2px 8px",
              borderRadius: "6px",
              background: "#f5f6fa",
              fontSize: 13,
              minWidth: 74,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                width: "13px",
                height: "13px",
                borderRadius: "50%",
                backgroundColor: "#27ae60",
                marginRight: "4px",
                border: "1px solid #e8f8f5",
              }}
            ></div>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: 13,
                  color: "#27ae60",
                  marginRight: 2,
                }}
              >
                C
              </span>
              <span style={{ color: "#222", fontSize: 12 }}>
                {Math.round(carbPercentage)}%
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getDishName = (dishObj) =>
    dishObj?.name?.[currentLanguage] || dishObj?.name?.["en"] || "-";

  // Sub-card component for protein/sauce dishes
  const SubDishCard = ({ subDish, label, borderColor, bgColor, onReplace }) => {
    if (!subDish) return null;
    return (
      <div
        style={{
          marginLeft: 40,
          marginTop: 4,
          marginBottom: 4,
          borderRadius: 10,
          border: `1px solid ${borderColor}`,
          background: bgColor,
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            color: "#555",
            minWidth: 70,
          }}
        >
          {label}
        </div>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 8,
            overflow: "hidden",
            flexShrink: 0,
            border: "1px solid #e8e8e8",
          }}
        >
          <img
            src={subDish?.img || "/images/photoForGoFit.jpg"}
            alt={getDishName(subDish)}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/images/photoForGoFit.jpg";
            }}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 13, display: "block" }} ellipsis>
            {getDishName(subDish)}
          </Text>
          <Space size={4} style={{ marginTop: 2 }}>
            <Tag
              style={{
                fontSize: 11,
                margin: 0,
                padding: "0 6px",
                borderRadius: 4,
              }}
            >
              {Math.round(Number(subDish?.weight) || 0)}g
            </Tag>
            <Tag
              color="orange"
              style={{
                fontSize: 11,
                margin: 0,
                padding: "0 6px",
                borderRadius: 4,
              }}
            >
              {Math.round(Number(subDish?.energy) || 0)} kcal
            </Tag>
            <Tag
              style={{
                fontSize: 11,
                margin: 0,
                padding: "0 6px",
                borderRadius: 4,
              }}
            >
              P:{Math.round(Number(subDish?.proteins) || 0)}
            </Tag>
            <Tag
              style={{
                fontSize: 11,
                margin: 0,
                padding: "0 6px",
                borderRadius: 4,
              }}
            >
              F:{Math.round(Number(subDish?.fats) || 0)}
            </Tag>
            <Tag
              style={{
                fontSize: 11,
                margin: 0,
                padding: "0 6px",
                borderRadius: 4,
              }}
            >
              C:{Math.round(Number(subDish?.carbohydrates) || Number(subDish?.carbs) || 0)}
            </Tag>
          </Space>
        </div>
        <Button
          size="small"
          type="default"
          onClick={(e) => {
            e.stopPropagation();
            onReplace();
          }}
          style={{
            borderColor: borderColor,
            color: borderColor,
            fontWeight: 600,
            fontSize: 12,
            borderRadius: 6,
          }}
        >
          {t("replace")}
        </Button>
      </div>
    );
  };

  const handleProteinReplace = (menuDish) => {
    setSelectedSubDishData(menuDish);
    setIsShowReplaceProtein(true);
  };

  const handleSauceReplace = (menuDish) => {
    setSelectedSubDishData(menuDish);
    setIsShowReplaceSauce(true);
  };

  const handleProteinDishSelected = (selectedDish) => {
    if (!selectedSubDishData) return;
    dispatch(
      createOrderActions.setOneReplacedProteinDish({
        mealId: selectedSubDishData.mealId,
        date: selectedSubDishData.date,
        packId,
        replacedTo: selectedDish?.dish?.id || selectedDish?.id,
        dish: selectedDish,
      })
    );
  };

  const handleSauceDishSelected = (selectedDish) => {
    if (!selectedSubDishData) return;
    dispatch(
      createOrderActions.setOneReplacedSauceDish({
        mealId: selectedSubDishData.mealId,
        date: selectedSubDishData.date,
        packId,
        replacedTo: selectedDish?.dish?.id || selectedDish?.id,
        dish: selectedDish,
      })
    );
  };

  if (isCustomMode) {
    return (
      <Col
        xs={24}
        md={16}
        lg={16}
        xl={16}
        xxl={16}
        style={{ maxHeight: "600px", overflowY: "auto" }}
      >
        <CustomMenuBuilder />
      </Col>
    );
  }

  return (
    <Col
      xs={24}
      md={16}
      lg={16}
      xl={16}
      xxl={16}
      style={{ maxHeight: "600px", overflowY: "auto" }}
    >
      {selectedDates.map((date) => (
        <div key={dayjs(date).format("YYYY-MM-DD")}>
          <h3>{dayjs(date).format("DD.MM.YYYY")}</h3>
          {/* nutrition summary */}
          <div
            style={{
              marginBottom: 20,
              background: "#fafbfc",
              borderRadius: 8,
              padding: 16,
              boxShadow: "0 1px 4px 0 #e3e6e8",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 16 }}>
              Daily Total
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px 16px",
              }}
            >
              <div>
                <span style={{ color: "#838383", marginRight: 4, fontSize: 13 }}>
                  Weight:
                </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {Math.round(getNutritionalSummaryForDate(date).totalWeight)} g
                </span>
              </div>
              <div>
                <span style={{ color: "#838383", marginRight: 4, fontSize: 13 }}>
                  Energy:
                </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {Math.round(getNutritionalSummaryForDate(date).totalEnergy)}{" "}
                  kcal
                </span>
              </div>
              <div>
                <span style={{ color: "#3498db", marginRight: 4, fontSize: 13 }}>
                  P:
                </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {Math.round(getNutritionalSummaryForDate(date).totalProteins)} g
                  <span style={{ color: "#3498db", marginLeft: 3, fontSize: 12 }}>
                    ({Math.round(getNutritionalSummaryForDate(date).proteinPercentage)}%)
                  </span>
                </span>
              </div>
              <div>
                <span style={{ color: "#f39c12", marginRight: 4, fontSize: 13 }}>
                  F:
                </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {Math.round(getNutritionalSummaryForDate(date).totalFats)} g
                  <span style={{ color: "#f39c12", marginLeft: 3, fontSize: 12 }}>
                    ({Math.round(getNutritionalSummaryForDate(date).fatPercentage)}%)
                  </span>
                </span>
              </div>
              <div>
                <span style={{ color: "#27ae60", marginRight: 4, fontSize: 13 }}>
                  C:
                </span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {Math.round(getNutritionalSummaryForDate(date).totalCarbs)} g
                  <span style={{ color: "#27ae60", marginLeft: 3, fontSize: 12 }}>
                    ({Math.round(getNutritionalSummaryForDate(date).carbPercentage)}%)
                  </span>
                </span>
              </div>
              <div>
                <MacronutrientPieChart
                  proteinPercentage={
                    getNutritionalSummaryForDate(date).proteinPercentage
                  }
                  fatPercentage={
                    getNutritionalSummaryForDate(date).fatPercentage
                  }
                  carbPercentage={
                    getNutritionalSummaryForDate(date).carbPercentage
                  }
                />
              </div>
            </div>
          </div>
          <List
            dataSource={getDishesForDate(date)}
            renderItem={(menuDish) => {
              const dish = menuDish?.dish;
              const displayName =
                dish?.name?.[currentLanguage] ||
                dish?.name?.["en"] ||
                "Unknown Dish";
              const isBase = menuDish?.type === DishType.BASE;
              const proteinDish = isBase ? getEffectiveProteinDish(menuDish) : null;
              const sauceDish = isBase ? getEffectiveSauceDish(menuDish) : null;

              return (
                <div key={menuDish.id || `${menuDish.mealId}-${menuDish.date}`}>
                  <List.Item
                    style={{ cursor: "pointer" }}
                    actions={[
                      <Button
                        type="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsShowUpdateSingleDish(true);
                          setSelectedDishId(menuDish.dish.id);
                        }}
                      >
                        {t("replace")}
                      </Button>,
                    ]}
                    onClick={() => {
                      setIsShowSingleDish(true);
                      setSelectedDishId(dish.id);
                    }}
                  >
                    <List.Item.Meta
                      avatar={<Avatar src={dish?.img} size={64} shape="square" />}
                      title={
                        <Space>
                          <Text strong>{displayName}</Text>
                          {isBase && (
                            <Tag
                              color="blue"
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                borderRadius: 999,
                                padding: "0 8px",
                              }}
                            >
                              {t("Base Dish")}
                            </Tag>
                          )}
                        </Space>
                      }
                      description={
                        <Space>
                          <Text type="secondary">{dish?.weight}g</Text>
                          <Text type="secondary">/</Text>
                          <Text type="secondary">{dish?.energy}kcal</Text>
                        </Space>
                      }
                    />
                  </List.Item>

                  {/* Protein/Sauce sub-cards for BASE dishes */}
                  {isBase && (
                    <div style={{ paddingBottom: 8 }}>
                      <SubDishCard
                        subDish={proteinDish}
                        label={t("Protein Dish")}
                        borderColor="#8bb5ff"
                        bgColor="#f4f8ff"
                        onReplace={() => handleProteinReplace(menuDish)}
                      />
                      <SubDishCard
                        subDish={sauceDish}
                        label={t("Sauce Dish")}
                        borderColor="#2f3a4d"
                        bgColor="#f7f8fa"
                        onReplace={() => handleSauceReplace(menuDish)}
                      />
                      {!proteinDish && !sauceDish && (
                        <div
                          style={{
                            marginLeft: 40,
                            marginTop: 4,
                            marginBottom: 4,
                            padding: "6px 12px",
                            fontSize: 12,
                            color: "#999",
                            fontStyle: "italic",
                          }}
                        >
                          {t("No protein or sauce dish assigned")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }}
            style={{ marginBottom: 16 }}
          />
        </div>
      ))}

      {isShowSingleDish && (
        <OrdersInfoSingleDishModal
          setIsShowSingleDish={setIsShowSingleDish}
          selectedDishId={selectedDishId}
        />
      )}

      {isShowUpdateSingleDish && (
        <SimpleReplaceDishModal
          dish={dishes.find((dish) => dish.dish.id === selectedDishId)}
          mealId={
            dishes.find((dish) => dish.dish.id === selectedDishId)?.mealId
          }
          packId={packId}
          date={dishes.find((dish) => dish.dish.id === selectedDishId)?.date}
          visible={isShowUpdateSingleDish}
          onClose={() => setIsShowUpdateSingleDish(false)}
        />
      )}

      {isShowReplaceProtein && selectedSubDishData && (
        <SimpleReplaceDishModal
          dish={{
            dish: getEffectiveProteinDish(selectedSubDishData),
          }}
          mealId={selectedSubDishData.mealId}
          packId={packId}
          date={selectedSubDishData.date}
          visible={isShowReplaceProtein}
          onClose={() => {
            setIsShowReplaceProtein(false);
            setSelectedSubDishData(null);
          }}
          dishType={DishType.PROTEIN}
          onSelectDish={handleProteinDishSelected}
        />
      )}

      {isShowReplaceSauce && selectedSubDishData && (
        <SimpleReplaceDishModal
          dish={{
            dish: getEffectiveSauceDish(selectedSubDishData),
          }}
          mealId={selectedSubDishData.mealId}
          packId={packId}
          date={selectedSubDishData.date}
          visible={isShowReplaceSauce}
          onClose={() => {
            setIsShowReplaceSauce(false);
            setSelectedSubDishData(null);
          }}
          dishType={DishType.SAUCE}
          onSelectDish={handleSauceDishSelected}
        />
      )}
    </Col>
  );
}

export default RightOrderCreateModel;
